import React, { useEffect, useState } from 'react';

import { Checkbox, FormHelperText, ListItemText, MenuItem } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { isEqual } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useContainer } from 'unstated-next';

import AddRowButton from '../../../../components/inputRow/AddRowButton';
import DeleteRowButton from '../../../../components/inputRow/DeleteRowButton';
import {
    StyledErrorIcon,
    StyledInputLabel,
    StyledRowContainer,
    StyledTextField,
    StyledRoleSelect
} from '../../../../styles/input-row';
import AdminAuthContainer from '../../AdminAuthContainer';
import { blankUser } from '../../constants/admin-users';
import { UserRole, systemRoles, User } from '../../constants/user-roles';
import { translateSelectedRoles } from '../../utils/translateSelectedRoles';
import {
    emailValidation,
    EmailErrorKeys,
    nameValidation,
    NameErrorKeys,
    roleValidation,
    RoleErrorKeys
} from '../../utils/userInputValidation';

const useStyles = makeStyles({
    error: {
        marginTop: '0px',
        display: 'flex'
    }
});

interface UsersRowProps {
    addRow: () => void;
    handleUserChange: (index: number, event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    handleRoleChange: (index: number, event) => void;
    removeRow: (index: number) => void;
    user: User;
    index: number;
    lastRow: boolean;
    hasErrors?: boolean;
    isDuplicateEmail?: boolean;
}

export const UsersRow: React.FC<UsersRowProps> = ({
    addRow,
    handleUserChange,
    handleRoleChange,
    removeRow,
    index,
    lastRow,
    user,
    hasErrors,
    isDuplicateEmail
}) => {
    const { authMode } = useContainer(AdminAuthContainer);
    const { t } = useTranslation(['portal', 'generic']);
    const [nameError, setNameError] = useState<NameErrorKeys>('');
    const [emailError, setEmailError] = useState<EmailErrorKeys>('');
    const [roleError, setRoleError] = useState<RoleErrorKeys>('');

    const classes = useStyles();

    const removeErrors = () => {
        setNameError('');
        setEmailError('');
        setRoleError('');
    };

    const blurValidation = (field: 'name' | 'email' | 'role') => {
        switch (field) {
            case 'name':
                if (isEqual(user, blankUser)) {
                    removeErrors();
                } else {
                    setNameError(nameValidation(user.name));
                }
                break;

            case 'email':
                if (isEqual(user, blankUser)) {
                    removeErrors();
                } else if (isDuplicateEmail) {
                    setEmailError('duplicate-email');
                } else {
                    setEmailError(emailValidation(user.email));
                }
                break;

            case 'role':
                if (isEqual(user, blankUser)) {
                    removeErrors();
                } else {
                    setRoleError(roleValidation(user.roles));
                }
                break;
            default:
                break;
        }
    };

    const renderErrorMessage = (errorKey: string) => {
        const errorMessage = t(`error.${errorKey}`);
        return (
            <>
                <StyledErrorIcon />
                {errorMessage}
            </>
        );
    };

    const renderRowButton = () => {
        console.log('renderRowButton', authMode);
        if (authMode !== 'admin') {
            return <></>;
        }
        if (lastRow) {
            return <AddRowButton addRow={addRow} />;
        }
        return <DeleteRowButton index={index} removeRow={removeRow} />;
    };

    useEffect(() => {
        if (hasErrors) {
            setNameError(nameValidation(user.name));
            if (isDuplicateEmail) {
                setEmailError('duplicate-email');
            } else {
                setEmailError(emailValidation(user.email));
            }
            setRoleError(roleValidation(user.roles));
        } else {
            removeErrors();
        }
    }, [user, hasErrors, isDuplicateEmail]);

    return (
        <>
            <StyledRowContainer>
                <div>
                    <StyledInputLabel>{t('generic:Name')}</StyledInputLabel>
                    <StyledTextField
                        name="name"
                        value={user.name}
                        variant="outlined"
                        size="small"
                        onChange={(event) => handleUserChange(index, event)}
                        onBlur={() => blurValidation('name')}
                        helperText={nameError === '' ? '' : renderErrorMessage(nameError)}
                        error={nameError !== ''}
                        FormHelperTextProps={{ classes: { error: classes.error } }}
                    />
                </div>
                <div>
                    <StyledInputLabel>{t('generic:Email')}</StyledInputLabel>
                    <StyledTextField
                        name="email"
                        value={user.email}
                        variant="outlined"
                        size="small"
                        onBlur={() => blurValidation('email')}
                        onChange={(event) => handleUserChange(index, event)}
                        helperText={emailError === '' ? '' : renderErrorMessage(emailError)}
                        error={emailError !== ''}
                        FormHelperTextProps={{ classes: { error: classes.error } }}
                    />
                </div>
                <div>
                    <StyledInputLabel>{t('generic:Role')}</StyledInputLabel>
                    <StyledRoleSelect
                        name="roles"
                        id="demo-multiple-checkbox"
                        multiple
                        value={user.roles}
                        onBlur={() => blurValidation('role')}
                        onChange={(event) => handleRoleChange(index, event)}
                        renderValue={(selected) => translateSelectedRoles(selected as UserRole[], t)}
                        error={roleError !== ''}
                    >
                        {systemRoles.map((roleName) => (
                            <MenuItem key={roleName} value={roleName}>
                                <Checkbox checked={user.roles.indexOf(roleName) > -1}></Checkbox>
                                <ListItemText primary={t(`users.userRoles.${roleName}`)} />
                            </MenuItem>
                        ))}
                    </StyledRoleSelect>
                    <FormHelperText sx={{ marginTop: '0px', display: 'flex' }} error>
                        {roleError === '' ? '' : renderErrorMessage(roleError)}
                    </FormHelperText>
                </div>
                {renderRowButton()}
            </StyledRowContainer>
        </>
    );
};
